/**
 * La fiche d'une commande, et la réception en deux temps (V1.6-09).
 *
 * Ce que cet écran tient, et qui est tout le ticket :
 *
 * * **créer un bon n'est pas le passer.** « Marquer commandée » est un geste
 *   séparé, parce que HD-Vault ne parle à aucun fournisseur : quelqu'un a
 *   téléphoné, écrit ou rempli un portail, et vient le dire ici ;
 * * **compter n'est pas recevoir.** Un brouillon de réception se corrige tant
 *   qu'il n'est pas finalisé ; une fois finalisé, le stock est entré et seule
 *   une transaction compensatoire le corrige ;
 * * **rien de tout cela n'est recalculé ici.** Reçu, reste, statut, totaux et
 *   même les droits (`can_receive`, `can_finalize`) viennent du serveur. Le
 *   frontend ne recalcule jamais la vérité du stock ;
 * * **une sur-réception se voit avant d'être refusée.** Le serveur annonce ce
 *   qui dépasse dès le brouillon ; finaliser exige alors une acceptation
 *   nommée, jamais une case « forcer » ;
 * * **renoncer à un reliquat est un geste, pas une correction** (V1.6-10A).
 *   Quatre colonnes cohabitent — commandé, reçu, abandonné, reste attendu — et
 *   aucune n'efface l'autre : la commande garde ce qu'on avait demandé, et on
 *   dit seulement qu'on ne l'attend plus. Aucun stock ne bouge.
 *
 * Le formulaire de comptage est pensé pour un iPhone sur un quai : une carte
 * par article, les champs empilés, un seul bouton en bas. Le reste dû est
 * **suggéré** — en gris, dans le champ — et jamais pré-rempli : un chiffre
 * pré-rempli qu'on valide sans regarder est un comptage que personne n'a fait.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2,
  CircleSlash,
  PackageCheck,
  Send,
  Truck,
  Undo2,
  XCircle,
} from "lucide-react";

import {
  useAbandonRemainder,
  useCancelPlannedOrder,
  useCreateDraftReceipt,
  useFinalizeReceipt,
  useMarkOrdered,
  usePlannedPurchaseOrder,
  useUpdateDraftReceipt,
} from "@/api/purchase-orders";
import { useLocationTree } from "@/api/queries";
import { useCanWrite } from "@/auth/useAuth";
import { useReturnableLines } from "@/api/purchase-orders";
import { useCreateSupplierReturn } from "@/api/supplier-returns";
import type {
  CountedLineInput,
  LocationNode,
  PlannedOrderLine,
  PlannedPurchaseOrderDetail,
  PlannedReceipt,
  RemainderReason,
  SupplierReturnLineInput,
  SupplierReturnReason,
  Uuid,
} from "@/api/types";
import { AcknowledgementCard } from "./AcknowledgementCard";
import { DispatchCard } from "./DispatchCard";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatAmount } from "@/lib/comparison-display";
import {
  formatDate,
  formatDateTime,
  formatPurchaseOrderStatus,
  formatQuantityWithUnit,
  formatRemainderReason,
  formatSupplierReturnReason,
  pluralize,
} from "@/lib/format";
import { reportMutationError } from "@/lib/mutation-errors";
import { purchaseOrderTone } from "@/lib/stock";

/** Le seul acquittement que ce ticket connaît, tel que le serveur le nomme. */
const OVER_RECEIPT_ACK = "OVER_RECEIPT";

export function PlannedOrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { data, isLoading, error, refetch } = usePlannedPurchaseOrder(orderId);

  if (isLoading) {
    return (
      <Page>
        <TableSkeleton rows={6} columns={5} />
      </Page>
    );
  }
  if (error || !data) {
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  }

  const { order } = data;
  return (
    <Page>
      <PageHeader
        title={order.code}
        subtitle={`${order.supplier_name}${order.quote_code ? ` · soumission ${order.quote_code}` : ""}`}
        actions={
          <StatusBadge tone={purchaseOrderTone(order.status)}>
            {formatPurchaseOrderStatus(order.status)}
          </StatusBadge>
        }
      />

      <div className="grid gap-4 @4xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          <OrderLines detail={data} />
          {data.can_receive && <ReceivingPanel detail={data} />}
          <Receipts detail={data} />
          <ReturnPanel detail={data} />
        </div>
        <div className="space-y-4">
          <Origin detail={data} />
          <Money detail={data} />
          <DispatchCard detail={data} />
          <AcknowledgementCard detail={data} />
          <Decisions detail={data} />
        </div>
      </div>
    </Page>
  );
}

/* -------------------------------------------------------------- ce qui est dû */

/**
 * Les quatre chiffres d'une ligne, et aucun n'est déduit ici.
 *
 * « Reste » et « Reste attendu » ne sont pas la même chose : le premier est ce
 * que le fournisseur doit encore sur le papier, le second ce qu'on attend
 * vraiment une fois le reliquat abandonné retiré. C'est le second qui décide de
 * ce qu'on peut encore recevoir, et le serveur le calcule.
 */
function OrderLines({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const [abandoning, setAbandoning] = useState<PlannedOrderLine | null>(null);

  const canAbandon = (line: PlannedOrderLine) =>
    detail.can_abandon_remainder && Number(line.still_expected) > 0;

  return (
    <Card>
      <CardHeader
        title="Lignes"
        subtitle="Reçu, abandonné et reste attendu sont calculés par le serveur à chaque lecture."
      />
      <div className="hidden md:block">
        <TableShell>
          <thead>
            <tr>
              <Th className="max-w-0">Article</Th>
              <Th align="right" className="whitespace-nowrap">
                Commandé
              </Th>
              <Th align="right" className="whitespace-nowrap">
                Reçu
              </Th>
              <Th align="right" className="whitespace-nowrap">
                Abandonné
              </Th>
              <Th align="right" className="whitespace-nowrap">
                Reste attendu
              </Th>
              <Th align="right" className="hidden whitespace-nowrap @3xl:table-cell">
                Prix
              </Th>
              <Th align="right" className="hidden whitespace-nowrap @3xl:table-cell">
                Montant
              </Th>
              <Th className="w-0" />
            </tr>
          </thead>
          <tbody>
            {detail.lines.map((line) => (
              <Tr key={line.id}>
                <Td className="max-w-0">
                  <span className="block truncate font-medium text-ink">
                    {line.description ?? "Article"}
                  </span>
                  {(line.manufacturer || line.mpn || line.supplier_sku) && (
                    <span className="block truncate text-[11px] text-ink-subtle">
                      {[line.manufacturer, line.mpn, line.supplier_sku]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  )}
                  {line.is_substitute && (
                    <StatusBadge tone="reserved" className="mt-1">
                      Équivalence
                    </StatusBadge>
                  )}
                </Td>
                <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
                  {formatQuantityWithUnit(line.quantity_ordered, line.unit)}
                </Td>
                <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
                  {formatQuantityWithUnit(line.received, line.unit)}
                </Td>
                <Td
                  align="right"
                  className={`tnum whitespace-nowrap text-[13px] ${
                    Number(line.abandoned) > 0 ? "text-warning" : "text-ink-subtle"
                  }`}
                >
                  {formatQuantityWithUnit(line.abandoned, line.unit)}
                </Td>
                <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
                  {formatQuantityWithUnit(line.still_expected, line.unit)}
                </Td>
                <Td
                  align="right"
                  className="tnum hidden whitespace-nowrap text-[13px] text-ink-muted @3xl:table-cell"
                >
                  {formatAmount(line.unit_price, detail.order.currency)}
                </Td>
                <Td
                  align="right"
                  className="tnum hidden whitespace-nowrap text-[13px] text-ink @3xl:table-cell"
                >
                  {formatAmount(line.merchandise_amount, detail.order.currency)}
                </Td>
                <Td className="w-0 whitespace-nowrap">
                  {canAbandon(line) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAbandoning(line)}
                    >
                      <CircleSlash className="size-4" aria-hidden />
                      Renoncer au reliquat
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      </div>

      <ul className="divide-y divide-border/60 md:hidden">
        {detail.lines.map((line) => (
          <li key={line.id} className="px-3 py-3">
            <p className="text-[14px] font-medium text-ink">{line.description ?? "Article"}</p>
            <dl className="tnum mt-1 grid grid-cols-2 gap-1 text-[12px]">
              <div>
                <dt className="text-ink-subtle">Commandé</dt>
                <dd className="text-ink">
                  {formatQuantityWithUnit(line.quantity_ordered, line.unit)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">Déjà reçu</dt>
                <dd className="text-ink-muted">
                  {formatQuantityWithUnit(line.received, line.unit)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">Abandonné</dt>
                <dd
                  className={
                    Number(line.abandoned) > 0 ? "text-warning" : "text-ink-subtle"
                  }
                >
                  {formatQuantityWithUnit(line.abandoned, line.unit)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">Reste attendu</dt>
                <dd className="text-ink">
                  {formatQuantityWithUnit(line.still_expected, line.unit)}
                </dd>
              </div>
            </dl>
            {canAbandon(line) && (
              <Button
                variant="ghost"
                size="sm"
                fullWidth
                className="mt-2"
                onClick={() => setAbandoning(line)}
              >
                <CircleSlash className="size-4" aria-hidden />
                Renoncer au reliquat
              </Button>
            )}
          </li>
        ))}
      </ul>

      {detail.remainder_actions.length > 0 && <RemainderJournal detail={detail} />}

      {abandoning && (
        <AbandonDialog
          orderId={detail.order.id}
          line={abandoning}
          onClose={() => setAbandoning(null)}
        />
      )}
    </Card>
  );
}

/**
 * Le journal des renoncements.
 *
 * Il est montré parce qu'un chiffre sans son geste n'explique rien : « 60
 * abandonnés » ne dit ni qui, ni quand, ni pourquoi, et c'est exactement ce
 * qu'on cherche six mois plus tard.
 */
function RemainderJournal({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const byLine = useMemo(() => {
    const found = new Map<Uuid, string>();
    for (const line of detail.lines) found.set(line.id, line.description ?? "Article");
    return found;
  }, [detail.lines]);

  return (
    <div className="border-t border-border/60 px-3 py-3">
      <p className="text-[12px] font-medium text-ink-muted">Reliquats abandonnés</p>
      <ul className="mt-2 space-y-1.5">
        {detail.remainder_actions.map((action) => (
          <li key={action.id} className="text-[12px] text-ink-muted">
            <span className="tnum text-ink">
              {formatQuantityWithUnit(action.quantity, action.unit)}
            </span>{" "}
            <span className="text-ink">{byLine.get(action.purchase_order_line_id)}</span> ·{" "}
            {formatRemainderReason(action.reason_code)}
            {action.reason_text ? ` — ${action.reason_text}` : ""} ·{" "}
            {formatDateTime(action.created_at)} · {action.created_by}
          </li>
        ))}
      </ul>
    </div>
  );
}

const REMAINDER_REASONS: RemainderReason[] = [
  "SUPPLIER_CANCELLED",
  "DISCONTINUED",
  "NO_LONGER_NEEDED",
  "DUPLICATE_ORDER",
  "CUSTOMER_CHANGE",
  "OTHER",
];

/**
 * Renoncer, en le disant.
 *
 * La quantité est pré-remplie avec le reste attendu parce que c'est le cas
 * ordinaire — « le reste ne viendra pas » — mais elle reste modifiable et
 * part explicitement : le serveur, sous verrou, refuse ce qui dépasse au
 * moment où il écrit, pas au moment où l'écran a lu.
 *
 * « Autre » exige une phrase. Sans elle, le motif ne dit rien à celui qui lira
 * la commande plus tard.
 */
function AbandonDialog({
  orderId,
  line,
  onClose,
}: {
  orderId: Uuid;
  line: PlannedOrderLine;
  onClose: () => void;
}) {
  const toast = useToast();
  const abandon = useAbandonRemainder();
  const [quantity, setQuantity] = useState(line.still_expected);
  const [reason, setReason] = useState<RemainderReason>("SUPPLIER_CANCELLED");
  const [text, setText] = useState("");

  const needsText = reason === "OTHER" && !text.trim();
  const positive = Number(quantity) > 0;

  const submit = () => {
    abandon.mutate(
      {
        orderId,
        line_id: line.id,
        quantity,
        reason_code: reason,
        reason_text: text.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(
            "Reliquat abandonné",
            "La commande n'attend plus cette quantité. Aucun stock n'a bougé.",
          );
          onClose();
        },
        onError: (error) => reportMutationError(toast, "Renoncement refusé", error),
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Renoncer au reliquat"
      description={`${line.description ?? "Article"} — reste attendu ${formatQuantityWithUnit(
        line.still_expected,
        line.unit,
      )}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!positive || needsText || abandon.isPending}
            loading={abandon.isPending}
          >
            Renoncer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-[12px] text-ink-muted">
          La commande garde ce qui avait été demandé. On dit seulement qu'on ne l'attend plus :
          aucun mouvement de stock, aucune réception inventée.
        </p>
        <Field label="Quantité abandonnée" htmlFor="abandon-quantity">
          <Input
            id="abandon-quantity"
            inputMode="decimal"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </Field>
        <Field label="Motif" htmlFor="abandon-reason">
          <Select
            id="abandon-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value as RemainderReason)}
          >
            {REMAINDER_REASONS.map((value) => (
              <option key={value} value={value}>
                {formatRemainderReason(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={reason === "OTHER" ? "Précisez (obligatoire)" : "Précision (facultatif)"}
          htmlFor="abandon-text"
        >
          <Input id="abandon-text" value={text} onChange={(event) => setText(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------- renvoyer au fournisseur */

const RETURN_REASONS: SupplierReturnReason[] = [
  "DAMAGED",
  "DEFECTIVE",
  "WRONG_ITEM",
  "OVER_DELIVERY",
  "NOT_NEEDED",
  "SUPPLIER_REQUEST",
  "OTHER",
];

/**
 * Préparer un retour, depuis ce qui est réellement arrivé.
 *
 * Le panneau part des **lignes de réception finalisées**, pas des lignes de
 * commande : on ne renvoie que ce qu'on a effectivement reçu, et c'est de là
 * que vient la quantité renvoyable. Le serveur la donne — reçu moins déjà
 * renvoyé — et l'écran ne la recalcule jamais.
 *
 * Ce que le panneau produit est un **brouillon**. Rien ne sort du stock ici :
 * la sortie a lieu à la finalisation, sur la fiche du retour, quand le serveur
 * revérifie sous verrou que la marchandise est là et n'est pas réservée
 * ailleurs.
 */
function ReturnPanel({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const toast = useToast();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const { data: returnable } = useReturnableLines(detail.order.id);
  const create = useCreateSupplierReturn();
  const [drafts, setDrafts] = useState<Record<string, { quantity: string; reason: string }>>({});
  const [rma, setRma] = useState("");

  const open = useMemo(
    () => (returnable ?? []).filter((item) => Number(item.returnable) > 0),
    [returnable],
  );

  // Un lecteur ne voit pas un formulaire qui lui serait refusé. C'est une
  // politesse : l'autorisation, elle, est vérifiée là où elle compte.
  if (!canWrite || open.length === 0) return null;

  const lines: SupplierReturnLineInput[] = open.flatMap((item) => {
    const entry = drafts[item.receipt_line_id];
    if (!entry || !(Number(entry.quantity) > 0)) return [];
    return [
      {
        product_id: item.product_id,
        quantity: entry.quantity,
        reason_code: entry.reason as SupplierReturnReason,
        receipt_line_id: item.receipt_line_id,
        purchase_order_line_id: item.purchase_order_line_id,
        unit: item.unit,
      },
    ];
  });

  const submit = () => {
    create.mutate(
      {
        supplier_id: detail.order.supplier_id,
        purchase_order_id: detail.order.id,
        rma_reference: rma.trim() || null,
        lines,
      },
      {
        onSuccess: (created) => {
          toast.success(
            "Retour préparé",
            "Rien n'est encore sorti du stock. Finalisez le retour pour envoyer la marchandise.",
          );
          setDrafts({});
          setRma("");
          navigate(`/retours-fournisseur/${created.supplier_return.id}`);
        },
        onError: (error) => reportMutationError(toast, "Retour refusé", error),
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="Renvoyer au fournisseur"
        subtitle="Renvoyable = reçu moins déjà renvoyé, calculé par le serveur. Préparer ne fait rien sortir."
      />
      <CardBody className="space-y-3">
        <p className="text-[12px] text-ink-muted">
          La réception d'origine reste telle quelle : ce qui est arrivé est arrivé. Un retour est
          un second fait, pas une correction du premier.
        </p>

        <ul className="space-y-2">
          {open.map((item) => {
            const entry = drafts[item.receipt_line_id] ?? { quantity: "", reason: "DAMAGED" };
            return (
              <li
                key={item.receipt_line_id}
                className="rounded-md border border-border/70 px-3 py-2.5"
              >
                <p className="text-[13px] font-medium text-ink">
                  {item.description ?? "Article"}
                </p>
                <p className="tnum text-[11px] text-ink-subtle">
                  reçu {formatQuantityWithUnit(item.received, item.unit)} · déjà renvoyé{" "}
                  {formatQuantityWithUnit(item.already_returned, item.unit)} · renvoyable{" "}
                  {formatQuantityWithUnit(item.returnable, item.unit)}
                </p>
                <div className="mt-2 grid gap-2 @md:grid-cols-2">
                  <Field
                    label="Quantité à renvoyer"
                    htmlFor={`return-qty-${item.receipt_line_id}`}
                  >
                    <Input
                      id={`return-qty-${item.receipt_line_id}`}
                      inputMode="decimal"
                      placeholder={item.returnable}
                      value={entry.quantity}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [item.receipt_line_id]: { ...entry, quantity: event.target.value },
                        }))
                      }
                    />
                  </Field>
                  <Field label="Motif" htmlFor={`return-reason-${item.receipt_line_id}`}>
                    <Select
                      id={`return-reason-${item.receipt_line_id}`}
                      value={entry.reason}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [item.receipt_line_id]: { ...entry, reason: event.target.value },
                        }))
                      }
                    >
                      {RETURN_REASONS.filter((value) => value !== "OTHER").map((value) => (
                        <option key={value} value={value}>
                          {formatSupplierReturnReason(value)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </li>
            );
          })}
        </ul>

        <Field label="Numéro d'autorisation du fournisseur (facultatif)" htmlFor="return-rma">
          <Input id="return-rma" value={rma} onChange={(event) => setRma(event.target.value)} />
        </Field>

        <Button
          variant="secondary"
          fullWidth
          disabled={lines.length === 0 || create.isPending}
          loading={create.isPending}
          onClick={submit}
        >
          <Undo2 className="size-4" aria-hidden />
          Préparer le retour
        </Button>
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------ compter et poser */

interface Counted {
  accepted: string;
  rejected: string;
  reason: string;
}

/**
 * Le formulaire du quai.
 *
 * Le reste dû s'affiche en suggestion parce que c'est le cas ordinaire : le
 * camion apporte ce qui manquait. Il n'est pas écrit dans le champ pour autant —
 * ce qui entre en stock doit être un chiffre que quelqu'un a compté, pas un
 * chiffre qu'on a laissé passer.
 */
function ReceivingPanel({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const toast = useToast();
  const create = useCreateDraftReceipt();
  const { data: locations } = useLocationTree();

  // Ce qu'on attend encore — reliquats abandonnés déduits. Sert à savoir si le
  // quai a encore quelque chose à faire, pas à cacher des lignes : de la
  // marchandise abandonnée peut arriver quand même, et refuser de la compter
  // rendrait l'inventaire faux pour protéger un chiffre. Elle se compte alors
  // comme une surprise, avec l'acquittement nommé de V1.6-09.
  const owing = useMemo(
    () => detail.lines.filter((line) => Number(line.still_expected) > 0),
    [detail.lines],
  );
  const [counts, setCounts] = useState<Record<Uuid, Counted>>({});
  const [locationId, setLocationId] = useState<string>("");
  const [reference, setReference] = useState("");

  const flat = useMemo(() => flatten(locations ?? []), [locations]);

  function valueFor(line: PlannedOrderLine): Counted {
    return counts[line.id] ?? { accepted: "", rejected: "", reason: "" };
  }

  function set(lineId: Uuid, patch: Partial<Counted>) {
    setCounts((current) => ({
      ...current,
      [lineId]: { ...(current[lineId] ?? { accepted: "", rejected: "", reason: "" }), ...patch },
    }));
  }

  const lines: CountedLineInput[] = useMemo(() => {
    const built: CountedLineInput[] = [];
    for (const line of detail.lines) {
      const counted = counts[line.id];
      if (!counted) continue;
      const accepted = counted.accepted.trim() || "0";
      const rejected = counted.rejected.trim() || "0";
      if (Number(accepted) === 0 && Number(rejected) === 0) continue;
      built.push({
        line_id: line.id,
        // Le déclaré est ce que le bordereau annonce ; à défaut d'une saisie
        // séparée, c'est ce qui a été compté au total.
        declared: String(Number(accepted) + Number(rejected)),
        accepted,
        rejected,
        location_id: locationId || null,
        mismatch_reason: counted.reason.trim() || null,
      });
    }
    return built;
  }, [counts, detail.lines, locationId]);

  async function submit() {
    try {
      await create.mutateAsync({
        orderId: detail.order.id,
        lines,
        supplierReference: reference,
      });
      toast.success(
        "Réception comptée",
        "Rien n'est encore entré en stock : vérifiez, puis finalisez.",
      );
      setCounts({});
      setReference("");
    } catch (error) {
      reportMutationError(toast, "Réception non enregistrée", error);
    }
  }

  if (owing.length === 0 && detail.lines.every((line) => Number(line.remaining) === 0)) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Recevoir maintenant"
        subtitle="Compter d'abord. Rien n'entre en stock avant la finalisation."
      />
      <CardBody className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Où ça va" htmlFor="receive-location">
            <Select
              id="receive-location"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Choisir un emplacement</option>
              {flat.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Bordereau du fournisseur" htmlFor="receive-reference">
            <Input
              id="receive-reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Numéro sur le papier"
            />
          </Field>
        </div>

        <ul className="space-y-3">
          {detail.lines.map((line) => {
            const counted = valueFor(line);
            return (
              <li key={line.id} className="rounded-md border border-border p-3">
                <p className="text-[14px] font-medium text-ink">
                  {line.description ?? "Article"}
                </p>
                <p className="tnum mt-0.5 text-[12px] text-ink-subtle">
                  Commandé {formatQuantityWithUnit(line.quantity_ordered, line.unit)} · déjà reçu{" "}
                  {formatQuantityWithUnit(line.received, line.unit)} · reste attendu{" "}
                  {formatQuantityWithUnit(line.still_expected, line.unit)}
                </p>
                {Number(line.abandoned) > 0 && (
                  <p className="tnum mt-0.5 text-[12px] text-warning">
                    {formatQuantityWithUnit(line.abandoned, line.unit)} abandonné :
                    ce qui arriverait quand même demandera une acceptation nommée.
                  </p>
                )}
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  <Field label="Accepté" htmlFor={`accepted-${line.id}`}>
                    <Input
                      id={`accepted-${line.id}`}
                      inputMode="decimal"
                      value={counted.accepted}
                      placeholder={line.remaining}
                      onChange={(event) => set(line.id, { accepted: event.target.value })}
                    />
                  </Field>
                  <Field label="Refusé" htmlFor={`rejected-${line.id}`}>
                    <Input
                      id={`rejected-${line.id}`}
                      inputMode="decimal"
                      value={counted.rejected}
                      onChange={(event) => set(line.id, { rejected: event.target.value })}
                    />
                  </Field>
                  <Field label="Pourquoi" htmlFor={`reason-${line.id}`}>
                    <Input
                      id={`reason-${line.id}`}
                      value={counted.reason}
                      placeholder="Boîtiers fendus…"
                      onChange={(event) => set(line.id, { reason: event.target.value })}
                    />
                  </Field>
                </div>
              </li>
            );
          })}
        </ul>

        <Button
          variant="primary"
          fullWidth
          onClick={() => void submit()}
          disabled={lines.length === 0 || !locationId || create.isPending}
        >
          <PackageCheck className="size-4" aria-hidden />
          {create.isPending ? "Enregistrement…" : "Enregistrer le comptage"}
        </Button>
        {lines.length > 0 && !locationId && (
          <p className="text-[12px] text-ink-subtle">
            Le stock accepté va exactement à un endroit : choisissez-le.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

interface FlatLocation {
  id: Uuid;
  label: string;
}

/** L'arbre des emplacements aplati, l'indentation gardée dans le libellé. */
function flatten(nodes: LocationNode[], depth = 0): FlatLocation[] {
  const found: FlatLocation[] = [];
  for (const node of nodes) {
    found.push({ id: node.id, label: `${"— ".repeat(depth)}${node.name}` });
    if (node.children.length > 0) found.push(...flatten(node.children, depth + 1));
  }
  return found;
}

/* ------------------------------------------------------------- les réceptions */

function Receipts({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  if (detail.receipts.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title="Réceptions"
        subtitle={`${pluralize(detail.receipts.length, "réception", "réceptions")} sur ce bon.`}
      />
      <CardBody className="space-y-3">
        {detail.receipts.map((receipt) => (
          <ReceiptRow key={receipt.id} receipt={receipt} detail={detail} />
        ))}
      </CardBody>
    </Card>
  );
}

/**
 * Une réception, brouillon ou scellée.
 *
 * Un brouillon porte son bouton « Finaliser ». S'il dépasse, le serveur le dit
 * en refusant une première fois — et la case d'acceptation apparaît alors, avec
 * ce qui dépasse, nommé et chiffré.
 */
function ReceiptRow({
  receipt,
  detail,
}: {
  receipt: PlannedReceipt;
  detail: PlannedPurchaseOrderDetail;
}) {
  const toast = useToast();
  const finalize = useFinalizeReceipt();
  const update = useUpdateDraftReceipt();
  const [accepting, setAccepting] = useState(false);

  const draft = receipt.status === "DRAFT";

  async function submit(acknowledgements: string[]) {
    try {
      await finalize.mutateAsync({ receiptId: receipt.id, acknowledgements });
      toast.success("Réception finalisée", "Le stock est entré. Le mouvement est écrit.");
      setAccepting(false);
    } catch (error) {
      // `OVER_RECEIPT_ACK_REQUIRED` n'est pas une panne : c'est le serveur qui
      // demande que quelqu'un assume l'excédent. On ouvre la boîte plutôt que
      // de crier à l'erreur.
      if (String((error as { message?: string }).message ?? "").includes("OVER_RECEIPT")) {
        setAccepting(true);
        return;
      }
      reportMutationError(toast, "Réception non finalisée", error);
    }
  }

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[12px] text-ink-muted">{receipt.code}</p>
          <p className="text-[12px] text-ink-subtle">
            {receipt.received_by} · {formatDateTime(receipt.received_at)}
            {receipt.supplier_reference ? ` · ${receipt.supplier_reference}` : ""}
          </p>
        </div>
        <StatusBadge tone={draft ? "info" : "available"} className="shrink-0">
          {draft ? "Brouillon" : "Finalisée"}
        </StatusBadge>
      </div>

      <ul className="tnum mt-2 space-y-0.5 text-[12px] text-ink">
        {receipt.lines.map((line) => (
          <li key={line.id}>
            {line.description ?? "Article"} :{" "}
            {formatQuantityWithUnit(line.quantity_accepted, line.unit)} accepté
            {Number(line.quantity_rejected) > 0 && (
              <>
                {", "}
                {formatQuantityWithUnit(line.quantity_rejected, line.unit)} refusé
                {line.mismatch_reason ? ` (${line.mismatch_reason})` : ""}
              </>
            )}
          </li>
        ))}
      </ul>

      {receipt.acknowledgements.length > 0 && (
        <ul className="mt-2 space-y-1 text-[12px] text-ink-muted">
          {receipt.acknowledgements.map((ack) => (
            <li key={`${ack.warning}-${ack.purchase_order_line_id}`}>
              Sur-réception de {ack.over_received_quantity} acceptée par {ack.acknowledged_by},{" "}
              {formatDateTime(ack.acknowledged_at)}
            </li>
          ))}
        </ul>
      )}

      {draft && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => void submit([])}
            disabled={finalize.isPending || update.isPending || !detail.can_receive}
          >
            <CheckCircle2 className="size-4" aria-hidden />
            {finalize.isPending ? "Finalisation…" : "Finaliser la réception"}
          </Button>
          <p className="self-center text-[12px] text-ink-subtle">
            Finaliser fait entrer le stock. Après, on corrige par un mouvement, pas par une
            réécriture.
          </p>
        </div>
      )}

      <Dialog
        open={accepting}
        onOpenChange={setAccepting}
        title="Cette livraison dépasse ce qui a été commandé"
        description="La marchandise est là et sera enregistrée. Quelqu'un doit dire qu'il l'assume."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAccepting(false)}>
              Annuler
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void submit([OVER_RECEIPT_ACK])}
              disabled={finalize.isPending}
            >
              J'accepte la sur-réception
            </Button>
          </div>
        }
      >
        <p className="text-[13px] text-ink-muted">
          Votre nom et l'heure seront enregistrés avec les quantités en cause. Il n'y a pas de
          « forcer » : accepter une sur-réception, c'est dire laquelle.
        </p>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------- la marge */

function Origin({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const { order } = detail;
  return (
    <Card>
      <CardHeader title="D'où vient ce bon" />
      <CardBody className="space-y-1 text-[13px]">
        <p className="text-ink">
          {order.supplier_name}
          {order.quote_code ? ` · ${order.quote_code}` : ""}
          {order.quote_revision && order.quote_revision > 1 ? ` rév. ${order.quote_revision}` : ""}
        </p>
        {order.source_purchase_plan_id && (
          <Link
            to={`/purchase-plans/${order.source_purchase_plan_id}`}
            className="block text-brand hover:underline"
          >
            Voir le plan d'achat approuvé
          </Link>
        )}
        <p className="text-ink-subtle">
          Créé par {order.created_by}, {formatDateTime(order.created_at)}
        </p>
        {order.ordered_at && (
          <p className="text-ink-subtle">
            Commandé par {order.ordered_by}, {formatDateTime(order.ordered_at)}
          </p>
        )}
        <p className="text-ink-subtle">
          Attendu : {order.expected_on ? formatDate(order.expected_on) : "date inconnue"}
        </p>
      </CardBody>
    </Card>
  );
}

function Money({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const { totals, currency } = detail.order;
  return (
    <Card>
      <CardHeader title="Montants" subtitle="Dans la devise du bon. Aucune conversion, aucune taxe." />
      <CardBody>
        <dl className="tnum space-y-1 text-[13px]">
          <div className="flex justify-between gap-3">
            <dt className="text-ink-muted">Marchandise</dt>
            <dd className="text-ink">{formatAmount(totals.merchandise, currency)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-muted">Transport</dt>
            <dd className="text-ink">
              {totals.freight === null ? (
                <span className="text-ink-subtle">Inconnu</span>
              ) : (
                formatAmount(totals.freight, currency)
              )}
            </dd>
          </div>
          <div className="flex justify-between gap-3 border-t border-border pt-1 font-medium">
            <dt className="text-ink">Total</dt>
            <dd className="text-ink">
              {totals.total === null ? (
                <span className="text-ink-subtle">Inconnu</span>
              ) : (
                formatAmount(totals.total, currency)
              )}
            </dd>
          </div>
        </dl>
        {totals.total === null && (
          <p className="mt-2 text-[12px] text-ink-subtle">
            Le fournisseur n'a pas chiffré son transport. Un total partiel présenté comme complet
            serait pire qu'une absence.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/** Les deux gestes qui décident du sort du bon. */
function Decisions({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const toast = useToast();
  const markOrdered = useMarkOrdered();
  const cancel = useCancelPlannedOrder();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");

  async function engage() {
    try {
      await markOrdered.mutateAsync({ orderId: detail.order.id });
      toast.success(
        "Commande passée",
        "Enregistré comme réellement commandée. Aucun stock n'a bougé.",
      );
    } catch (error) {
      reportMutationError(toast, "Commande non marquée", error);
    }
  }

  async function abandon() {
    try {
      await cancel.mutateAsync({ orderId: detail.order.id, reason });
      toast.success("Commande annulée", "Le bon reste lisible : une commande abandonnée en est une.");
      setConfirming(false);
      setReason("");
    } catch (error) {
      reportMutationError(toast, "Commande non annulée", error);
    }
  }

  return (
    <Card>
      <CardHeader title="Décider" />
      <CardBody className="space-y-2">
        {detail.can_mark_ordered && (
          <>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              onClick={() => void engage()}
              disabled={markOrdered.isPending}
            >
              <Send className="size-4" aria-hidden />
              {markOrdered.isPending ? "Enregistrement…" : "Marquer commandée"}
            </Button>
            <p className="text-[12px] text-ink-subtle">
              Pour une commande passée ailleurs — au téléphone, sur un portail. Si HD-Vault
              doit la transmettre, utilisez « Envoyer ce bon ».
            </p>
          </>
        )}

        {detail.order.status === "ORDERED" && !detail.can_mark_ordered && (
          <p className="flex items-center gap-1.5 text-[13px] text-ink-muted">
            <Truck className="size-4" aria-hidden />
            En route chez le fournisseur.
          </p>
        )}

        {detail.can_cancel && (
          <Button variant="ghost" size="sm" fullWidth onClick={() => setConfirming(true)}>
            <XCircle className="size-4" aria-hidden />
            Annuler la commande
          </Button>
        )}
        {!detail.can_cancel && detail.order.status !== "CANCELLED" && (
          <p className="text-[12px] text-ink-subtle">
            Une partie de cette commande est arrivée : elle ne s'annule plus.
          </p>
        )}
      </CardBody>

      <Dialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Annuler cette commande"
        description="Le bon reste lisible. Ce qui serait déjà arrivé ne s'effacerait pas."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Revenir
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => void abandon()}
              disabled={!reason.trim() || cancel.isPending}
            >
              Annuler la commande
            </Button>
          </div>
        }
      >
        <Field label="Pourquoi" htmlFor="cancel-reason" hint="Une annulation dit pourquoi.">
          <Input
            id="cancel-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Chantier reporté"
          />
        </Field>
      </Dialog>
    </Card>
  );
}
